import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, str, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints, ApiError } from '../api/client.js';
import { navigate } from '../router.js';
import type { MfaMethodDto, PasswordPolicyDto } from '../api/types.js';
import '../components/sso-card.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';
import '../components/sso-button.js';
import '../components/sso-text-field.js';

type ValueEl = HTMLElement & { value: string };

/**
 * `<security-page>` — self-service security settings (route `/security`). Lets the signed-in user manage
 * their 2FA methods (list / remove / add), regenerate recovery codes and change their password. Wraps the
 * `/api/mfa/*` and `/api/account/change-password` endpoints. Adding a method reuses the enrollment pages.
 */
@customElement('security-page')
@localized()
export class SecurityPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .section {
        margin: var(--sso-space-lg) 0;
      }
      h2 {
        font-size: 1.05rem;
        margin: 0 0 var(--sso-space-sm);
      }
      .methods {
        display: grid;
        gap: 0.5rem;
      }
      .method {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.6rem 0.8rem;
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        background: var(--sso-color-surface);
      }
      .method .ico {
        font-size: 1.4rem;
        flex: none;
      }
      .method .meta {
        flex: 1;
      }
      .method .meta .desc {
        color: var(--sso-color-muted);
        font-size: 0.85rem;
      }
      .row {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        margin-top: var(--sso-space-sm);
      }
      .muted {
        color: var(--sso-color-muted);
      }
      .codes {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.4rem;
        font-family: var(--sso-font-mono);
        margin: var(--sso-space-sm) 0;
      }
      .field {
        margin: 0.5rem 0;
      }
      ul.rules {
        list-style: none;
        padding: 0;
        margin: 0.25rem 0 0;
        font-size: 0.9rem;
      }
      ul.rules li {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        color: var(--sso-color-muted);
      }
      ul.rules li.ok {
        color: var(--sso-color-success);
      }
      .back {
        display: inline-block;
        margin-top: var(--sso-space);
        color: var(--sso-color-brand);
      }
    `,
  ];

  @state() private loading = true;
  @state() private methods: MfaMethodDto[] = [];
  @state() private recoveryRemaining: number | null = null;
  @state() private newCodes: string[] | null = null;
  @state() private error = '';
  @state() private pwError = '';
  @state() private pwOk = false;
  @state() private busy = false;
  @state() private pw = '';
  // Fallback mirrors the server defaults (Sso:PasswordPolicy); replaced by the fetched values.
  @state() private policy: PasswordPolicyDto = { minLength: 12, maxLength: 72 };

  @query('#cur') private curField?: ValueEl;
  @query('#nw') private nwField?: ValueEl;
  @query('#cf') private cfField?: ValueEl;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    await this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const [methods, rec, policy] = await Promise.all([
        endpoints.mfaMethods(),
        endpoints.mfaRecovery(),
        endpoints.passwordPolicy(),
      ]);
      this.methods = methods;
      this.recoveryRemaining = rec.remaining;
      this.policy = policy;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return; // client bounces to /login
      this.error = msg('Could not load your security settings. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  private async removeMethod(id: number): Promise<void> {
    this.busy = true;
    try {
      await endpoints.mfaRemoveMethod(id);
      await this.load();
    } catch {
      this.error = msg('Could not remove that method. Please try again.');
    } finally {
      this.busy = false;
    }
  }

  private async regenerate(): Promise<void> {
    this.busy = true;
    try {
      const r = await endpoints.mfaRecoveryRegenerate();
      this.newCodes = r.codes;
      this.recoveryRemaining = r.codes.length;
    } catch {
      this.error = msg('Could not regenerate recovery codes. Please try again.');
    } finally {
      this.busy = false;
    }
  }

  /* --- password policy helpers (mirror reset-password-page; the server is authoritative) --- */
  private get hasLength(): boolean {
    return this.pw.length >= this.policy.minLength;
  }
  private get withinMax(): boolean {
    return this.pw.length <= this.policy.maxLength;
  }
  private get hasMixedCase(): boolean {
    return /[a-z]/.test(this.pw) && /[A-Z]/.test(this.pw);
  }
  private get hasDigit(): boolean {
    return /\d/.test(this.pw);
  }
  private get meetsPolicy(): boolean {
    return this.hasLength && this.withinMax && this.hasMixedCase && this.hasDigit;
  }

  /** Map a change-password failure to a specific, localized message via the backend's error code. */
  private changePasswordError(err: unknown): string {
    if (err instanceof ApiError && err.status === 400) {
      switch (err.code) {
        case 'current_password_incorrect':
          return msg('Your current password is incorrect. Please re-enter it.');
        case 'password_too_short':
          return msg(str`Your new password is too short — it must be at least ${this.policy.minLength} characters long.`);
        case 'password_too_long':
          return msg(str`Your new password is too long — it can be at most ${this.policy.maxLength} characters.`);
        case 'password_missing_character_classes':
          return msg('Your new password must include an upper-case letter, a lower-case letter and a digit.');
        default:
          return msg('Your password could not be changed — check the requirements under the new-password field.');
      }
    }
    return msg('Could not change your password. Please try again.');
  }

  private async changePassword(): Promise<void> {
    this.pwError = '';
    this.pwOk = false;
    const cur = this.curField?.value ?? '';
    const nw = this.nwField?.value ?? '';
    const cf = this.cfField?.value ?? '';
    if (!cur || !nw) {
      this.pwError = msg('Please fill in all fields.');
      return;
    }
    if (!this.meetsPolicy) {
      this.pwError = msg('Your new password does not meet all requirements yet — see the checklist under the new-password field.');
      return;
    }
    if (nw !== cf) {
      this.pwError = msg('The new passwords do not match.');
      return;
    }
    this.busy = true;
    try {
      await endpoints.changePassword(cur, nw);
      this.pwOk = true;
      if (this.curField) this.curField.value = '';
      if (this.nwField) this.nwField.value = '';
      if (this.cfField) this.cfField.value = '';
      this.pw = '';
    } catch (err) {
      this.pwError = this.changePasswordError(err);
    } finally {
      this.busy = false;
    }
  }

  private renderRule(ok: boolean, text: string) {
    return html`<li class=${ok ? 'ok' : ''}>
      <span aria-hidden="true">${ok ? '✓' : '•'}</span>${text}
    </li>`;
  }

  private renderMethod(m: MfaMethodDto) {
    const isPasskey = m.kind === 'passkey';
    return html`<div class="method">
      <span class="ico" aria-hidden="true">${isPasskey ? '🔑' : '🔢'}</span>
      <span class="meta">
        <div>${m.displayName ?? (isPasskey ? msg('Passkey') : msg('Authenticator app'))}</div>
        <div class="desc">
          ${isPasskey ? msg('Passkey') : msg('Authenticator app (TOTP)')}
          ${m.lastUsedUtc ? html` · ${msg('last used')} ${new Date(m.lastUsedUtc).toLocaleDateString()}` : nothing}
        </div>
      </span>
      <sso-button variant="danger" ?disabled=${this.busy} @click=${() => this.removeMethod(m.id)}
        >${msg('Remove')}</sso-button
      >
    </div>`;
  }

  override render() {
    if (this.loading) return html`<sso-card><sso-spinner></sso-spinner></sso-card>`;

    return html`<sso-card>
      <h1>${msg('Security')}</h1>
      ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}

      <div class="section">
        <h2>${msg('Two-step verification methods')}</h2>
        ${this.methods.length === 0
          ? html`<p class="muted">${msg('You have no second-factor methods yet.')}</p>`
          : html`<div class="methods">${this.methods.map((m) => this.renderMethod(m))}</div>`}
        <div class="row">
          <sso-button variant="secondary" @click=${() => navigate('/enroll/totp')}
            >${msg('Add authenticator app')}</sso-button
          >
          <sso-button variant="secondary" @click=${() => navigate('/enroll/passkey')}
            >${msg('Add passkey')}</sso-button
          >
        </div>
      </div>

      <div class="section">
        <h2>${msg('Recovery codes')}</h2>
        <p class="muted">
          ${this.recoveryRemaining === null
            ? nothing
            : msg(html`${this.recoveryRemaining} unused recovery codes remaining.`)}
        </p>
        ${this.newCodes
          ? html`<sso-alert kind="warning"
                >${msg('Store these codes now — they are shown only once.')}</sso-alert
              >
              <div class="codes">${this.newCodes.map((c) => html`<span>${c}</span>`)}</div>`
          : nothing}
        <div class="row">
          <sso-button variant="secondary" ?disabled=${this.busy} @click=${() => this.regenerate()}
            >${msg('Regenerate recovery codes')}</sso-button
          >
        </div>
      </div>

      <div class="section">
        <h2>${msg('Change password')}</h2>
        ${this.pwError ? html`<sso-alert kind="error">${this.pwError}</sso-alert>` : nothing}
        ${this.pwOk ? html`<sso-alert kind="success">${msg('Your password has been changed.')}</sso-alert>` : nothing}
        <div class="field">
          <sso-text-field id="cur" type="password" label=${msg('Current password')}></sso-text-field>
        </div>
        <div class="field">
          <sso-text-field
            id="nw"
            type="password"
            label=${msg('New password')}
            autocomplete="new-password"
            @input-change=${(e: CustomEvent<string>) => {
              this.pw = e.detail;
            }}></sso-text-field>
          <ul class="rules">
            ${this.renderRule(this.hasLength, msg(str`At least ${this.policy.minLength} characters`))}
            ${this.renderRule(this.hasMixedCase, msg('Upper and lower case letters'))}
            ${this.renderRule(this.hasDigit, msg('At least one digit'))}
            ${this.withinMax
              ? nothing
              : this.renderRule(false, msg(str`At most ${this.policy.maxLength} characters`))}
          </ul>
        </div>
        <div class="field">
          <sso-text-field id="cf" type="password" label=${msg('Confirm new password')}></sso-text-field>
        </div>
        <div class="row">
          <sso-button variant="primary" ?disabled=${this.busy} @click=${() => this.changePassword()}
            >${msg('Change password')}</sso-button
          >
        </div>
      </div>

      <a class="back" href="/" @click=${(e: Event) => {
        e.preventDefault();
        navigate('/');
      }}>${msg('← Back to applications')}</a>
    </sso-card>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'security-page': SecurityPage;
  }
}
